import { Slide } from "@jelly/lesson";
import config from "appConfig";

const principle: Slide = {
  title: "Two impedance method",
  layout: {
    type: "customCase010106",
    options: {
      views: [
        {
          pages: [
            {
              title: "Scheme of the LAUM/CTTM️ device",
              image: `${config.ASSETS_URL}/01/case-01/slide06-view01-image01.svg`,
            },
            {
              title: "Picture of the LAUM/CTTM device",
              image: `${config.ASSETS_URL}/01/case-01/slide06-view01-image02.svg`,
            },
          ],
          equationsImages: [
            `${config.ASSETS_URL}/01/case-01/slide06-view01-equation01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view01-equation02.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view01-equation03.svg`,
          ],
          list: [
            "The quantity measured by the device is the transfer function between the 2 microphones (microphone 1 in the back cavity and microphone 2 in the front cavity)",
            `<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
              <msub>
                <mi>e</mi>
                <mn scriptlevel=0>1</mn>
              </msub>
            </math>
            and
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
              <msub>
                <mi>e</mi>
                <mn scriptlevel=0>2</mn>
              </msub>
            </math>
            are the Fourier transforms of the microphones voltage signals. The impedance is then obtained with`,
            "Variables in this expression of the impedance given by the sensor are defined as:",
            `
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
              <mi>K</mi>
            </math>,
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
              <msub>
                <mi>L</mi>
                <mn>2</mn>
              </msub>
            </math>
            and
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
              <msubsup>
                <mi>L</mi>
                <mn>2</mn>
                <mo>"</mo>
              </msubsup>
            </math>
            are complex frequency-dependent constant obtained from a calibration. <br />
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
              <msub>
                <mi>Z</mi>
                <mi>c</mi>
              </msub>
              <mo>=</mo>
              <mfrac>
                <mrow>
                  <mi scriptlevel=0>ρ</mi>
                  <mi scriptlevel=0>c</mi>
                </mrow>
                <mi scriptlevel=0>S</mi>
              </mfrac>
            </math>, with
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
              <mi>S</mi>
            </math>
            the input cross section of the measured object
            `,
          ],
        },
        {
          description: `As shown later, the device geometry imposes the material to be symmetrical. In that case, we have seen (see Characterizing a symmetrical two-port, part 7 of Definition and characterizing methods) that the characteristics matrices have only two unknowns because of the reciprocity and symmetry hypothesis. Hence, only 2 measurements (FRF) with 2 different termination impedances are needed.<br /><br />
          In addition to the previously-mentioned 2 measurements, a calibration step of the sensor requires 1 measurement (FRF) and the “anechoic” termination impedance (ZA) has also to be measured, adding one more measurement to do (FRF). Note that the second impedance is assumed to be infinite.`,
          listDescription: "Are therefore needed to characterize absorbing materials with the two impedance method:",
          list: [
            "the previously-cited 4 measurements of FRF for getting the sensor calibration, the anechoic termination impedance and the two impedances with the sample, one with a rigid wall and another one with the (characterized) anechoic termination,",
            "the formulas to calculate the elements of the scattering matrix,",
            "the formulas allowing to calculate the equivalent compressibility and equivalent density.",
          ],
          footer:
            "Each acquisition step of an FRF is now illustrated. Results are shown and then used to characterize the tested absorbing material.",
        },
        {
          pages: [
            {
              title: "Scheme of the Impedance head",
              image: `${config.ASSETS_URL}/01/case-01/slide06-view03-image01.svg`,
            },
            {
              title: "Impedance sensor",
              image: `${config.ASSETS_URL}/01/case-01/slide06-view03-image02.svg`,
            },
            {
              title: "Positioning of the perfectly rigid wall",
              image: `${config.ASSETS_URL}/01/case-01/slide06-view03-image03.svg`,
            },
            {
              title: "Setup ready for measurement",
              image: `${config.ASSETS_URL}/01/case-01/slide06-view03-image04.svg`,
            },
          ],
          image: `${config.ASSETS_URL}/01/case-01/slide06-view03-charts.svg`,
          description: "This result allows to calibrate the impedance head and hence to obtain the value for",
          equationImage: `${config.ASSETS_URL}/01/case-01/slide06-view03-equation.svg`,
        },
        {
          images: [
            `${config.ASSETS_URL}/01/case-01/slide06-view04-image01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view04-image02.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view04-image03.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view04-image04.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view04-image05.svg`,
          ],
          descriptions: [
            "Anechoic termination and impedance sensor with the sample holder without the sample",
            "Positioning of the anechoic termination",
            "Positioning of the impedance sensor",
            "Setup ready for measurement",
          ],
        },
        {
          images: [
            `${config.ASSETS_URL}/01/case-01/slide06-view05-image01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view05-image02.svg`,
          ],
          equationImage: `${config.ASSETS_URL}/01/case-01/slide06-view05-equation.svg`,
        },
        {
          images: [
            `${config.ASSETS_URL}/01/case-01/slide06-view06-image01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view06-image02.svg`,
          ],
          equationImage: `${config.ASSETS_URL}/01/case-01/slide06-view06-equation.svg`,
        },
        {
          images: [
            `${config.ASSETS_URL}/01/case-01/slide06-view07-image01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view07-image02.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view07-image03.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view07-image04.svg`,
          ],
          descriptions: [
            "Positioning of the sample on the holder",
            "Positioning of the sample holder on the rigid wall",
            "Setup ready for measurement",
          ],
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide06-view08-image.svg`,
        },
        {
          images: [
            `${config.ASSETS_URL}/01/case-01/slide06-view09-image01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view09-image02.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view09-image03.svg`,
            `${config.ASSETS_URL}/01/case-01/slide06-view09-image04.svg`,
          ],
          descriptions: [
            "Positioning of the anechoic termination",
            "Positioning of the sensor with sample holder on the anechoic termination",
            "Setup ready for measurement",
          ],
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide06-view10-image.svg`,
        },
        {
          pages: [
            {
              image: `${config.ASSETS_URL}/01/case-01/slide06-view11-image01.svg`,
            },
            {
              image: `${config.ASSETS_URL}/01/case-01/slide06-view11-image02.svg`,
            },
          ],
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide06-view12-image.svg`,
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide06-view13-image.svg`,
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide06-view14-image.svg`,
          list: [
            "Both methods match in the higher frequency range<br />(400-5000 Hz)",
            "The four microphone method fail for the low frequency because the pressure difference between microphones is to small for low frequencies (f < 0.05 c/2d).",
            "The two impedance method is limited by the signal-to-noise ratio in the low frequency range (below 50 Hz).",
          ],
        },
      ],
    },
  },
};

export default principle;
